import * as React from 'react'
import { translate } from '../../../utils/translations'
import * as translations from './translations'
import { colors } from './colors'
import { Line } from './line'
import { useHashParam } from './use-hash-param'
import { Header } from './header'
import { Voter } from './types'
import { xhr } from '../../../xhr'
import { useAuthToken } from './login'
import { besuchenNotwendigTags, besuchenUnnötigTags } from './tags'
import { Text } from '../../elementals/Text'

/*

  const allData = lowercaseSearch
    ? (data ?? []).filter(
        voter =>
          voter.ort.toLocaleLowerCase().includes(lowercaseSearch) ||
          voter.strasse.toLocaleLowerCase().includes(lowercaseSearch) ||
          voter.name.toLocaleLowerCase().includes(lowercaseSearch) ||
          voter.reserviertVon?.toLocaleLowerCase().includes(lowercaseSearch) ||
          String(voter.geburtsjahr).includes(lowercaseSearch) ||
          (voter.istEU && 'EU' === lowercaseSearch) ||
          (voter.adresszusatz &&
            voter.adresszusatz.toLocaleLowerCase().includes(lowercaseSearch)) ||
          voter.tags.some(tag => tag.toLocaleLowerCase().includes(lowercaseSearch)),
      )
    : data ?? []

    */

const useVoters = (authToken: string | undefined) => {
  const [{ data, lastUpdated }, setData] = React.useState<{
    data: Array<Voter> | undefined
    lastUpdated: number
  }>({ data: undefined, lastUpdated: 0 })

  const trigger = React.useCallback(async () => {
    if (authToken) {
      const response = await xhr.get('/api/voters', {
        headers: {
          Authorization: 'Bearer ' + authToken,
        },
      })
      setData({ data: response.data, lastUpdated: new Date().valueOf() })
    }
  }, [setData, authToken])

  React.useEffect(() => {
    trigger()
  }, [trigger])

  return {
    data,
    lastUpdated,
    isLoading: data === undefined,
    retrigger: trigger,
  }
}

const SaveElementsTopPosition = ({ firstLineTop, ort }: { firstLineTop: number; ort: string }) => {
  const [trigger, retrigger] = React.useState({})
  const viewId = localStorage.getItem(ort)

  React.useEffect(() => {
    const coordinates = [100, firstLineTop] as const

    const viewIdAtTopOfScreen =
      document
        .elementsFromPoint(...coordinates)
        .find(element => element.getAttributeNames().includes('data-line-id'))
        ?.getAttribute('data-line-id') ?? undefined

    if (viewIdAtTopOfScreen && viewIdAtTopOfScreen !== viewId) {
      localStorage.setItem(ort, viewIdAtTopOfScreen)
    }

    const timeout = setTimeout(() => {
      retrigger({})
    }, 2000)

    return () => {
      clearTimeout(timeout)
    }
  }, [trigger, firstLineTop, viewId, ort])

  return null
}

const useScrollIntoView = (querySelector: string | undefined, retries: number) => {
  const [hasScrolledIntoViewRetries, setHasScrolledIntoViewRetries] = React.useState<number>(0)
  const hasRetriesLeft = hasScrolledIntoViewRetries < retries
  React.useEffect(() => {
    if (querySelector && hasRetriesLeft) {
      const element = document.querySelector(querySelector)
      if (element) {
        window.scrollTo({ top: element.getBoundingClientRect().top - 50, behavior: 'instant' })
        setHasScrolledIntoViewRetries(retries)
      } else {
        setTimeout(() => {
          setHasScrolledIntoViewRetries(prev => prev + 1)
        }, 100)
      }
    }
  }, [querySelector, hasRetriesLeft, retries, hasScrolledIntoViewRetries])

  return [
    querySelector ? !hasRetriesLeft : true,
    React.useCallback(() => {
      setHasScrolledIntoViewRetries(0)
    }, [setHasScrolledIntoViewRetries]),
  ] as const
}

export const Page = () => {
  React.useEffect(() => {
    document.title = translate({ translations, path: ['gw'] })
  }, [])

  const authToken = useAuthToken()

  const { data: allData, lastUpdated, retrigger } = useVoters(authToken)
  const username = useHashParam('user')

  const myReservationsText = translate({ translations, path: ['myReservations'] })

  let doIHaveReservations = false
  const orte = allData
    ? [myReservationsText].concat(
        allData.reduce((acc, { ort, reserviertVon }) => {
          if (reserviertVon === username) {
            doIHaveReservations = true
          }
          return acc.includes(ort) ? acc : acc.concat(ort)
        }, [] as Array<string>),
      )
    : []

  const hashOrt = useHashParam('ort')
  const [ortState, setOrtOriginal] = React.useState<string>()
  const ort =
    ortState ?? hashOrt ?? localStorage.getItem('ort') ?? (doIHaveReservations ? orte[0] : orte[1])

  const hashViewId = useHashParam('viewId')
  const [viewIdState, setViewId] = React.useState<string>()
  const viewId = viewIdState ?? hashViewId ?? localStorage.getItem(ort)

  const [hasScrolledIntoView, reset] = useScrollIntoView(
    viewId ? `[data-line-id="${viewId}"]` : undefined,
    20,
  )

  const setOrt = React.useCallback(
    newOrt => {
      reset()
      if (ort && viewId) {
        localStorage.setItem(ort, viewId)
      }
      const newOrtViewId = localStorage.getItem(newOrt) ?? undefined
      if (newOrtViewId) {
        setViewId(newOrtViewId)
      }
      setOrtOriginal(newOrt)
    },
    [ort, viewId, setOrtOriginal, reset],
  )

  const [showDone, setShowDone] = React.useState(true)
  const [showReserved, setShowReserved] = React.useState(true)
  const [showOnlyWithComments, setShowOnlyWithComments] = React.useState(false)
  const [tagSearchTerm, setTagSearchTerm] = React.useState('')

  const tags = (allData ?? [])
    .flatMap(({ tags }) => tags ?? [])
    .reduce((acc, current) => {
      const index = acc.findIndex(({ tag }) => tag === current)
      if (index !== -1) {
        acc[index] = { ...acc[index], occurrences: acc[index].occurrences + 1 }
      } else {
        acc.push({ tag: current, occurrences: 1 })
      }
      return acc
    }, [] as Array<{ occurrences: number; tag: string }>)
    .sort((a, b) => {
      const sort = b.occurrences - a.occurrences
      return sort === 0 ? b.tag.localeCompare(a.tag) : sort
    })
    .map(({ tag }) => tag)

  const ortData = allData?.filter(person =>
    ort === myReservationsText ? person.reserviertVon === username : person.ort === ort,
  )

  const ortDataForUser = ortData?.filter(
    ({ istAbgehandeltWorden, reserviertVon, tags }) =>
      (showDone ? true : !istAbgehandeltWorden) &&
      (showReserved ? true : [null, '', username].includes(reserviertVon)) &&
      (showOnlyWithComments ? tags.length > 0 : true) &&
      (tagSearchTerm ? tags.some(tag => tag.includes(tagSearchTerm)) : true),
  )

  const openPeople = ortData?.filter(({ istAbgehandeltWorden }) => istAbgehandeltWorden === 0)
  const isOrtDone = openPeople?.length === 0
  const isRestReserved =
    (openPeople?.length ?? 0) > 0 &&
    openPeople?.every(
      ({ reserviertVon }) =>
        reserviertVon !== '' && reserviertVon !== null && reserviertVon !== username,
    )

  const showOrt = ort === myReservationsText

  const firstLineTop = React.useRef<number>()

  console.log({ hasScrolledIntoView, ort, viewId })

  return (
    <div
      style={{
        backgroundColor: colors.pageBackground,
        color: colors.text.default,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        zoom: '150%',
        visibility: hasScrolledIntoView ? 'visible' : 'hidden',
      }}
    >
      {hasScrolledIntoView && (
        <SaveElementsTopPosition ort={ort} firstLineTop={firstLineTop.current ?? 50} />
      )}
      <Header
        ref={node => {
          const clientRect = node?.getBoundingClientRect()

          if (clientRect) {
            firstLineTop.current = clientRect.bottom + 10
          }
        }}
        data={allData}
        lastUpdated={lastUpdated}
        ortData={ortData}
        orte={orte}
        retrigger={retrigger}
        selectedOrt={ort}
        setOrt={setOrt}
        setShowDone={setShowDone}
        setShowOnlyWithComments={setShowOnlyWithComments}
        setTagSearchTerm={setTagSearchTerm}
        setShowReserved={setShowReserved}
        showDone={showDone}
        showOnlyWithComments={showOnlyWithComments}
        tagSearchTerm={tagSearchTerm}
        showReserved={showReserved}
      />
      <datalist id="tags">
        {tags.map(tag => (
          <option key={tag}>{tag}</option>
        ))}
      </datalist>
      {(isOrtDone || isRestReserved) && (
        <div
          style={{
            position: 'sticky',
            top: 10,
            backgroundColor: colors.pageBackground,
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: 'auto',
            justifyContent: 'center',
            rowGap: 8,
            alignItems: 'center',
            padding: 16,
          }}
        >
          <Text style={{ fontSize: '1.5em', color: colors.text.default, wordBreak: 'break-word' }}>
            {!doIHaveReservations && ort === myReservationsText
              ? translate({ translations, path: ['youHaveNoReservations'], passed: ort })
              : isRestReserved
              ? translate({ translations, path: ['yourWorkDone'], passed: ort })
              : translate({ translations, path: ['allWorkDone'], passed: ort })}
          </Text>
          {!showReserved && isRestReserved && (
            <button
              onClick={() => {
                setShowReserved(true)
                setShowDone(false)
              }}
            >
              {translate({ translations, path: ['showReservedByOther'] })}
            </button>
          )}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '2px',
        }}
      >
        {ortDataForUser?.map(voter => (
          <Line
            key={voter.id}
            {...voter}
            retrigger={retrigger}
            username={username}
            authToken={authToken}
            showOrt={showOrt}
          />
        ))}
      </div>
    </div>
  )
}
